import {
  Container,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  Box,
  Button,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { getCategory } from '../api/categoryApi';
import { getQuestionsByDomain } from '../api/questionApi';
import QuestionTooltip from './QuestionTooltip';
import { submitAnswer } from '../api/answersApi';
import { categoryLevels, levelColors } from './CategoryLevels';
import { getAllPatientReports } from '../api/answersApi';
import dayjs from 'dayjs';

const getLevelForQuestion = (domainCode, questionNumber) => {
  const domainLevels = categoryLevels.find((c) => c.category === domainCode);
  if (!domainLevels) return null;

  for (const lvl of domainLevels.levels) {
    if (questionNumber <= lvl.maxLevel) {
      return lvl.level;
    }
  }
  return null;
};

const validationSchema = Yup.object({
  responses: Yup.object().test(
    'at-least-one',
    'Please answer at least one question.',
    (value) =>
      Object.values(value || {}).some((v) => v.answer !== undefined || v.pr !== undefined)
  )
});

const QUESTIONS_PER_PAGE = 5;

const DomainForm = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [domain, setDomain] = useState(null);
  const [domainsLoaded, setDomainsLoaded] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionLoading, setQuestionLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [staffName, setStaffName] = useState('');
  const [staffId, setStaffId] = useState('');
  const [reports, setReports] = useState([]);
  const [lastDomainReport, setLastDomainReport] = useState(null);

  const patientId = location.state?.patientId || '';
  const patientName = location.state?.patientName || '';
  
  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await getAllPatientReports(patientId);
        if (res.data.succeeded) {
          setReports(res.data.data);
        }
      } catch (error) {
        console.error('Failed to load reports:', error);
      }
    };

    if (patientId) fetchReports();
  }, [patientId]);

  useEffect(() => {
    if (!reports.length || !code) return;

    const domainReports = reports
      .map((r) => ({
        createdAt: r.createdAt,
        domainData: r.domains.find((d) => d.domain.code === code),
      }))
      .filter((r) => r.domainData);

    if (domainReports.length > 0) {
      domainReports.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const latest = domainReports[0];
      setLastDomainReport({
        ...latest.domainData,
        createdAt: latest.createdAt,
      });
    }
  }, [reports, code]);

  useEffect(() => {
    const storage = JSON.parse(localStorage.getItem('info')) || '';
    const storedName = storage ? storage.name : '';
    const storedId = storage ? storage.userId : '';
    if (!storedName) {
      navigate('/', { replace: true });
    } else {
      setStaffName(storedName);
      setStaffId(storedId);
    }
  }, [navigate]);

  useEffect(() => {
    if (!code) return;

    getCategory()
      .then((res) => {
        const allDomains = res.data?.data || [];
        const match = allDomains.find((d) => d.code === code);
        setDomain(match || null);
        setDomainsLoaded(true);
        if (match) {
          return getQuestionsByDomain(code);
        } else {
          throw new Error('Invalid domain');
        }
      })
      .then((res) => {
        const rawQuestions = res.data?.data || [];
        const formatted = rawQuestions.map((q) => ({
          id: q.id,
          text: q.question,
          description: q.description?.replace(/\r/g, '') || ''
        }));
        setQuestions(formatted);
      })
      .catch((err) => {
        console.error('Error fetching domain/questions:', err);
        if (err.message === 'Network Error') {
          alert('Network error. Please check your connection and try again.');
        }
        setDomain(null);
      })
      .finally(() => setQuestionLoading(false));
  }, [code]);

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);
  const currentQuestions = questions.slice(
    currentPage * QUESTIONS_PER_PAGE,
    (currentPage + 1) * QUESTIONS_PER_PAGE
  );

  const formik = useFormik({
    initialValues: {
      staffName: staffName,
      responses: lastDomainReport
      ? lastDomainReport.answers.reduce((acc, ans) => {
          acc[ans.questionId] = {
            answer: ans.staffResponse,
            pr: ans.parentResponse,
          };
          return acc;
        }, {})
      : {},
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const res = {
        patientId: patientId,
        staffName: staffName,
        createdBy: staffId,
        domain: domain,
        answers: Object.entries(formik.values.responses).map(([id, resp]) => ({
          questionId: Number(id),
          staffResponse: resp.answer,
          parentResponse: resp.pr
        }))
      };

      const response = await submitAnswer(res);
      if (response.data.data.success) {
        setOpenDialog(true);
      }
    }
  });

  // Find the highest index question that has any answer
  const lastAttendedIndex = questions.reduce((maxIdx, q, idx) => {
    const resp = formik.values.responses[q.id];
    if (resp && (resp.answer !== undefined || resp.pr !== undefined)) {
      return idx; // update last attended
    }
    return maxIdx;
  }, -1);

  const getQuestionColor = (resp, idx, lastAttendedIndex, domainCode) => {
    if ((domainCode === 'SEN' || domainCode === 'BEH') && resp) {
      if (resp.answer === 2 && resp.pr === 2) {
        return '#f44336'; // Red
      }
      if (resp.answer !== 2 || resp.pr !== 2) {
        return '#ff9800'; // Orange
      }
      return 'black'; // Reset
    }

    if (!resp || (resp.answer === undefined && resp.pr === undefined)) {
      // Mark as skipped only if it's BEFORE last attended
      if (idx <= lastAttendedIndex) {
        return '#f44336'; // Red
      }
      return 'inherit'; // Not attempted yet (future question)
    }
    if (resp.answer !== 2 || resp.pr !== 2) {
      return '#ff9800'; // Orange
    }
    return 'inherit';
  };

  const handleChange = (id, field, value) => {
    formik.setFieldValue(`responses.${id}`, {
      ...(formik.values.responses[id] || {}),
      questionId: id,
      [field]: Number(value)
    });
  };

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  if (domainsLoaded && !domain) {
    return <Navigate to="/404" replace />;
  }

  if (!domainsLoaded || questionLoading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading domain and questions...</Typography>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box textAlign="right" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate(`/domains`, {
              replace: true,
              state: { patientId, patientName }
            })}
          >
            Domains
          </Button>
        </Box>
        <form onSubmit={formik.handleSubmit}>
          {patientId &&
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Patient: <strong>{patientName}</strong> (ID: <strong>{patientId}</strong>)
            </Typography>
          }

          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
            {domain.name} ({domain.code})
            <Chip
              label={lastDomainReport ? "Follow-up" : "New"}
              size="small"
              color={lastDomainReport ? "secondary" : "success"}
              sx={{ ml: 1, fontWeight: "bold" }}
            />
            {lastDomainReport && (
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 2, color: "text.secondary" }}
              >
                Last Assessment: {dayjs(lastDomainReport.createdAt).format("MMM D, YYYY")}
              </Typography>
            )}
          </Typography>

          {formik.errors.responses && typeof formik.errors.responses === 'string' && (
            <Alert severity="error" sx={{ mb: 2 }}>{formik.errors.responses}</Alert>
          )}

          {currentQuestions.map((q, i) => {
            const globalIndex = currentPage * QUESTIONS_PER_PAGE + i;
            const questionNumber = globalIndex + 1;
            const level = getLevelForQuestion(domain.code, questionNumber);

            return (
              <Box
                key={q.id}
                sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography
                    variant="body1"
                    sx={{
                      mr: 1,
                      color: getQuestionColor(
                        formik.values.responses[q.id],
                        globalIndex,          // the question index
                        lastAttendedIndex,     // last attended
                        domain.code
                      )
                    }}
                  >
                    <strong>Q{questionNumber}:</strong> {q.text}
                    {q.description && <QuestionTooltip description={q.description} />}
                  </Typography>


                  {/* Level Badge */}
                  {level && (
                    <Tooltip title={`Level ${level}`}>
                      <Chip
                        label={`L${level}`}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: levelColors[level] || '#999', // fallback grey
                          color: '#fff',
                          height: 24,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                
                {/* Existing radio groups... */}
                <Grid container spacing={2} mt={2}>
                  {/* Doctor */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">Doctor</Typography>
                    <FormControl>
                      <RadioGroup
                        row
                        value={formik.values.responses[q.id]?.answer ?? ''}
                        onChange={(e) => handleChange(q.id, 'answer', e.target.value)}
                      >
                        <FormControlLabel value="2" control={<Radio />} label="Y" />
                        <FormControlLabel value="1" control={<Radio />} label="I" />
                        <FormControlLabel value="0" control={<Radio />} label="N" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Parent */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" fontWeight="bold">Parent</Typography>
                    <FormControl>
                      <RadioGroup
                        row
                        value={formik.values.responses[q.id]?.pr ?? ''}
                        onChange={(e) => handleChange(q.id, 'pr', e.target.value)}
                      >
                        <FormControlLabel value="2" control={<Radio />} label="Y" />
                        <FormControlLabel value="1" control={<Radio />} label="I" />
                        <FormControlLabel value="0" control={<Radio />} label="N" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            );
          })}

          {patientId &&
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={4}
              flexWrap="wrap"
              gap={2}
            >
              {/* Left: Previous + Next */}
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handlePrevious}
                  disabled={currentPage === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage >= totalPages - 1}
                  sx={{
                    backgroundColor: '#ea258b',
                    '&:hover': { backgroundColor: '#d11e77' }
                  }}
                >
                  Next
                </Button>
              </Box>

              {/* Center: Page indicator */}
              <Box flexGrow={1} textAlign="center">
                <Typography variant="body2">
                  Page {currentPage + 1} of {totalPages}
                </Typography>
              </Box>

              {/* Right: Submit */}
              <Box>
                <Button
                  variant="contained"
                  onClick={formik.handleSubmit}
                  sx={{
                    backgroundColor: '#ea258b',
                    '&:hover': { backgroundColor: '#d11e77' }
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          }
        </form>
      </Container>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Assessment Submitted</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to continue to another module or view the report?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ borderColor: '#ea258b', '&:hover': { borderColor: '#d11e77' } }}
            onClick={() => {
              setOpenDialog(false);
              navigate('/domains', { state: { patientId, patientName } });
            }}
          >
            Continue to Next Module
          </Button>
          <Button
            sx={{ backgroundColor: '#ea258b', '&:hover': { backgroundColor: '#d11e77' } }}
            onClick={() => {
              setOpenDialog(false);
              navigate('/report', {
                state: {
                  patientId,
                  patientName,
                  staffName,
                  code
                }
              });
            }}
            variant="contained"
          >
            View Report
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DomainForm;
